import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { CircleNotch, CheckCircle, Package, Wrench, Truck, XCircle, Warning } from "@phosphor-icons/react";

const STEPS = [
  { key: "recibido", label: "Recibido", icon: Package },
  { key: "produccion", label: "En producción", icon: Wrench },
  { key: "listo", label: "Listo / Enviado", icon: Truck },
];

export default function OrderStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/order-status/${token}`, { withCredentials: false })
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center px-6" data-testid="order-status-notfound">
        <div className="text-center">
          <XCircle size={56} weight="fill" className="text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold mt-4">Pedido no encontrado</h1>
          <p className="text-neutral-400 mt-2">Verifica el enlace que te enviamos por correo.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center">
        <CircleNotch size={40} className="animate-spin text-gold" />
      </div>
    );
  }

  const activeStep = data.cancelled ? -1 : data.step;

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 px-5 py-10" data-testid="order-status-page">
      <div className="max-w-xl mx-auto">
        <p className="text-gold text-sm font-medium">{data.business_name}</p>
        <h1 className="text-3xl font-bold mt-1">Estado de tu pedido</h1>
        <div className="flex items-center gap-2 mt-2 text-neutral-400">
          <span className="font-mono text-neutral-200" data-testid="order-status-number">{data.order_number}</span>
          {data.buyer_name && <span>· {data.buyer_name}</span>}
        </div>

        {data.cancelled ? (
          <div className="mt-8 border border-red-500/30 bg-red-500/10 rounded-2xl p-6 text-center" data-testid="order-status-cancelled">
            <XCircle size={48} weight="fill" className="text-red-400 mx-auto" />
            <p className="mt-3 font-semibold text-red-200">Este pedido fue cancelado</p>
          </div>
        ) : (
          <div className="mt-10">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-6 left-0 right-0 h-0.5 bg-white/10" />
              <div className="absolute top-6 left-0 h-0.5 bg-gold transition-all" style={{ width: activeStep <= 0 ? "0%" : activeStep === 1 ? "50%" : "100%" }} />
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = i <= activeStep;
                return (
                  <div key={s.key} className="relative z-10 flex flex-col items-center flex-1" data-testid={`order-step-${s.key}`}>
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center border-2 transition-colors ${done ? "bg-gold border-gold text-obsidian" : "bg-[#151519] border-white/15 text-neutral-500"}`}>
                      {done && i < activeStep ? <CheckCircle size={24} weight="fill" /> : <Icon size={22} weight={done ? "fill" : "regular"} />}
                    </div>
                    <span className={`mt-2 text-xs text-center ${done ? "text-neutral-100" : "text-neutral-500"}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.needs_deposit_for_production && (
          <div className="mt-8 border border-amber-400/30 bg-amber-400/10 rounded-xl p-4 flex gap-3" data-testid="order-status-deposit-note">
            <Warning size={22} className="text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100">
              Tu pedido pasará a <b>producción</b> cuando se reciba al menos el <b>50%</b> del pago.
              <div className="mt-1 text-amber-200/80">Pagado: ${Number(data.amount_paid).toFixed(2)} de ${Number(data.total).toFixed(2)} ({data.pct_paid}%).</div>
            </div>
          </div>
        )}

        {/* Payment summary */}
        <div className="mt-8 border border-white/10 rounded-2xl p-5 bg-white/[0.03]" data-testid="order-status-payment">
          <div className="flex justify-between text-sm py-1"><span className="text-neutral-400">Total</span><span>${Number(data.total).toFixed(2)}</span></div>
          <div className="flex justify-between text-sm py-1"><span className="text-neutral-400">Pagado</span><span className="text-emerald-300">${Number(data.amount_paid).toFixed(2)}</span></div>
          {data.balance > 0.001 && <div className="flex justify-between text-sm py-1 border-t border-white/10 mt-1 pt-2"><span className="text-amber-300 font-medium">Balance pendiente</span><span className="text-amber-300 font-medium">${Number(data.balance).toFixed(2)}</span></div>}
        </div>

        {/* Items */}
        <div className="mt-6 space-y-4">
          {(data.items || []).map((it, i) => (
            <div key={i} className="border border-white/10 rounded-2xl p-5 bg-white/[0.03]" data-testid={`order-status-item-${i}`}>
              <div className="font-semibold">{it.quantity}× {it.name}</div>
              <div className="text-sm text-neutral-400 mt-1 space-x-3">
                {it.material && <span>Material: <span className="text-neutral-200">{it.material}</span></span>}
                {it.size && <span>Talla: <span className="text-neutral-200">{it.size}</span></span>}
              </div>
              {it.customization_text && <div className="text-sm text-neutral-400 mt-1">Personalización: <span className="text-neutral-200">{it.customization_text}</span></div>}
              {(it.design_image_url || (it.custom_logo_urls && it.custom_logo_urls.length > 0)) && (
                <div className="flex flex-wrap gap-3 mt-3">
                  {it.design_image_url && <img src={it.design_image_url} alt="Diseño" className="h-28 w-28 object-cover rounded-lg border border-white/15" />}
                  {(it.custom_logo_urls || []).map((u, k) => <img key={k} src={u} alt="Logo" className="h-20 w-20 object-contain rounded-lg border border-white/15 bg-white/5 p-1" />)}
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-center text-neutral-600 text-xs mt-10">Pedido de {data.business_name}</p>
      </div>
    </div>
  );
}
